import { type FormEvent, useEffect, useId, useState } from "react";
import { ApiError, api } from "./api.ts";
import { Modal } from "./Dialog.tsx";
import { buyPass } from "./paying.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button } from "./souvenir.ts";
import { ButtonLink } from "./ui.tsx";

// Free Trip Passes from codes (features/paying.md, "Free Trip Passes from codes"): "Have a code?" beside every Get a
// Trip Pass button opens a small sheet, "Redeem a code", one field and Redeem; /redeem/CODE opens it over Your trips
// with the code filled in (App.tsx). A campaign's code and a one-off code alike: the pass waits on Your trips, which
// shows it at once (its `trips` event). Refused, the server's words say what to do; a discount code comes with Get a
// Trip Pass carrying it, and an invite's code goes on to its own page.

// The longest code the field takes (src/billing/free-passes.ts, MAX_CODE_LENGTH).
const MAX_CODE = 64;

// Open the sheet from anywhere on the page, with a code or empty.
const OPEN = "tripmo:redeem";
export function openRedeem(code = ""): void {
  dispatchEvent(new CustomEvent<string>(OPEN, { detail: code }));
}
// Said once a pass is redeemed, for what lists passes beside Your trips (the profile's Billing).
export const REDEEMED_EVENT = "tripmo:redeemed";

// /redeem, or /redeem/CODE (signing in first when signed out: src/app.ts): the code it carries, and the address to
// leave instead (Your trips', /); null on any other address.
export function redeemByAddress(): { code: string } | null {
  const m = location.pathname.match(/^\/redeem(?:\/([^/]+))?\/?$/);
  if (!m) return null;
  let code = "";
  try {
    code = decodeURIComponent(m[1] ?? "");
  } catch {}
  return { code: code.slice(0, MAX_CODE) };
}

// The sheet, kept outside the app's own drawing (main.tsx), so it opens over whatever the page shows.
export function RedeemHost() {
  const [open, setOpen] = useState<{ code: string; n: number } | null>(null);
  useEffect(() => {
    const on = (e: Event) => setOpen((was) => ({ code: (e as CustomEvent<string>).detail, n: (was?.n ?? 0) + 1 }));
    addEventListener(OPEN, on);
    return () => removeEventListener(OPEN, on);
  }, []);
  if (!open) return null;
  return <RedeemDialog key={open.n} code={open.code} onClose={() => setOpen(null)} />;
}

// "Have a code?", beside a Get a Trip Pass button.
export function HaveACode({ className }: { className?: string }) {
  return (
    <button
      type="button"
      className={`tm-link-button callout tm-have-code${className ? ` ${className}` : ""}`}
      onClick={() => openRedeem()}
    >
      Have a code?
    </button>
  );
}

// Why it wasn't redeemed: a discount code's refusal says where to use it (Get a Trip Pass with the code on it, where
// it's a Trip Pass's discount); one of the refusals the server knows (`known`) is the person's to act on, never a bug
// to report.
type Refused = { message: string; discount: { code: string } | null; known: boolean };

function RedeemDialog({ code: given, onClose }: { code: string; onClose: () => void }) {
  const fieldId = useId();
  const [code, setCode] = useState(given);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Refused | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const typed = code.trim();
    if (!typed) {
      setError({ message: "Type the code first.", discount: null, known: true });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const answer = await api.redeem(typed);
      if (!answer.redeemed) {
        location.assign(answer.invite);
        return;
      }
      setDone(answer.message);
      dispatchEvent(new Event(REDEEMED_EVENT));
    } catch (err) {
      const data = err instanceof ApiError ? err.data : {};
      const discount =
        data["state"] === "discount" && data["product"] === "trip_pass" && typeof data["discount"] === "string"
          ? { code: data["discount"] }
          : null;
      setError({
        message: err instanceof Error ? err.message : "Couldn't redeem it. Try again.",
        discount,
        known: typeof data["state"] === "string" || data["limit"] !== undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  // Back here after buying with the code: this page, without a code an address may carry.
  const here = location.pathname.startsWith("/redeem") ? "/" : location.pathname;
  const withCode = (href: string, discount: string) => `${href}&code=${encodeURIComponent(discount)}`;
  return (
    <Modal
      title={done ? "Free Trip Pass" : "Redeem a code"}
      busy={busy}
      focus={done ? ".tm-redeem-done" : `[id="${fieldId}"]`}
      onCancel={onClose}
      className="tm-redeem-dialog"
    >
      {({ describedBy }) =>
        done ? (
          <>
            <p id={describedBy} className="callout" role="status">
              {done}
            </p>
            <div className="tm-dialog-actions">
              <Button variant="primary" className="tm-redeem-done" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <form className="tm-redeem-form" onSubmit={submit} noValidate>
            <p id={describedBy} className="callout tm-muted">
              A code gives you a free <span className="tm-nowrap">Trip Pass</span>: one trip, for up to 5 people, you
              included.
            </p>
            <label className="sv-field" htmlFor={fieldId}>
              <span className="sv-field-label">Code</span>
              <input
                id={fieldId}
                className="sv-field-input tm-redeem-code"
                value={code}
                maxLength={MAX_CODE}
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                placeholder="BETALIST"
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            {error && (
              <div className="tm-redeem-error">
                <p className="callout tm-error" role="alert">
                  {error.message}
                  {!error.known && <ReportThis />}
                </p>
                {error.discount && (
                  <ButtonLink href={withCode(buyPass(here), error.discount.code)} variant="secondary" size="sm">
                    Get a Trip Pass
                  </ButtonLink>
                )}
              </div>
            )}
            <div className="tm-dialog-actions">
              <Button variant="secondary" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? "Redeeming…" : "Redeem"}
              </Button>
            </div>
          </form>
        )
      }
    </Modal>
  );
}
